/**
 * Web Heart Rate Detector
 * Canvas-based RGB pixel extraction with CHROM-compatible output.
 * Optimized: reuses canvases, crops to bounding box for ROI extraction.
 */

import { HeartRateAlgorithm } from '@alphahub/eie-core';
import type { IHeartRateDetector, HeartRateData } from '@alphahub/eie-core';

const LEFT_CHEEK = [205, 50, 117, 118, 101, 36, 205];
const RIGHT_CHEEK = [425, 280, 346, 347, 330, 266, 425];

// Landmark indices for motion tracking (nose tip + ear tragions for yaw detection)
const NOSE_TIP = 1;
const LEFT_EAR = 234;
const RIGHT_EAR = 454;
const DESKTOP_MOTION_GATE_THRESHOLD = 0.005;
const MOBILE_MOTION_GATE_THRESHOLD = 0.009;
const MOTION_EMA_ALPHA = 0.35;

export class WebHeartRateDetector implements IHeartRateDetector {
  private algorithm: HeartRateAlgorithm;
  private mainCanvas: HTMLCanvasElement | null = null;
  private mainCtx: CanvasRenderingContext2D | null = null;
  private roiCanvas: HTMLCanvasElement | null = null;
  private roiCtx: CanvasRenderingContext2D | null = null;
  private prevNose: { x: number; y: number; z: number } | null = null;
  private prevLeftEar: { x: number; y: number; z: number } | null = null;
  private prevRightEar: { x: number; y: number; z: number } | null = null;
  private motionEma?: number;

  constructor(calibrationSeconds: number = 120) {
    this.algorithm = new HeartRateAlgorithm(calibrationSeconds);
    const threshold = this.isLikelyMobileDevice()
      ? MOBILE_MOTION_GATE_THRESHOLD
      : DESKTOP_MOTION_GATE_THRESHOLD;
    this.algorithm.setMotionGateThreshold(threshold);
  }

  setCalibrationDuration(seconds: number): void {
    this.algorithm.setCalibrationDuration(seconds);
  }

  reset(): void {
    this.algorithm.reset();
    this.prevNose = null;
    this.prevLeftEar = null;
    this.prevRightEar = null;
    this.motionEma = undefined;
  }

  private isLikelyMobileDevice(): boolean {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent ?? '' : '';
    const byUA = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
    const byPointer = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(pointer: coarse)').matches;
    return byUA || byPointer;
  }

  captureFrame(
    videoElement: HTMLVideoElement,
    landmarks: Array<{ x: number; y: number; z: number }>
  ): HeartRateData | null {
    if (!landmarks || landmarks.length < 468) return null;

    const motion = this.computeMotion(landmarks);
    const rgb = this.extractCheekRGB(videoElement, landmarks);
    if (rgb === null) return null;

    return this.algorithm.addSample(rgb.r, rgb.g, rgb.b, motion);
  }

  /**
   * Compute head motion magnitude from landmark deltas frame-over-frame.
   * Uses nose tip for translation and ear-to-ear distance change for yaw rotation.
   * Returns a normalized magnitude (0..1 scale, matching landmark coordinates).
   * Returns undefined on the first frame (no previous data to compare).
   */
  private computeMotion(
    landmarks: Array<{ x: number; y: number; z: number }>
  ): number | undefined {
    const nose = landmarks[NOSE_TIP];
    const leftEar = landmarks[LEFT_EAR];
    const rightEar = landmarks[RIGHT_EAR];

    if (!this.prevNose || !this.prevLeftEar || !this.prevRightEar) {
      this.prevNose = { ...nose };
      this.prevLeftEar = { ...leftEar };
      this.prevRightEar = { ...rightEar };
      return undefined;
    }

    // Translation: Euclidean distance of nose tip displacement (x, y)
    const dx = nose.x - this.prevNose.x;
    const dy = nose.y - this.prevNose.y;
    const translation = Math.sqrt(dx * dx + dy * dy);

    // Rotation proxy: change in ear-to-ear distance indicates yaw
    const prevEarDist = Math.sqrt(
      (this.prevRightEar.x - this.prevLeftEar.x) ** 2 +
      (this.prevRightEar.y - this.prevLeftEar.y) ** 2
    );
    const currEarDist = Math.sqrt(
      (rightEar.x - leftEar.x) ** 2 +
      (rightEar.y - leftEar.y) ** 2
    );
    const rotation = Math.abs(currEarDist - prevEarDist);

    this.prevNose = { ...nose };
    this.prevLeftEar = { ...leftEar };
    this.prevRightEar = { ...rightEar };

    const rawMotion = translation + rotation;
    this.motionEma = this.motionEma === undefined
      ? rawMotion
      : MOTION_EMA_ALPHA * rawMotion + (1 - MOTION_EMA_ALPHA) * this.motionEma;
    return this.motionEma;
  }

  private ensureMainCanvas(w: number, h: number): CanvasRenderingContext2D | null {
    if (!this.mainCanvas || this.mainCanvas.width !== w || this.mainCanvas.height !== h) {
      this.mainCanvas = document.createElement('canvas');
      this.mainCanvas.width = w;
      this.mainCanvas.height = h;
      this.mainCtx = this.mainCanvas.getContext('2d');
    }
    return this.mainCtx;
  }

  private ensureROICanvas(w: number, h: number): CanvasRenderingContext2D | null {
    if (!this.roiCanvas || this.roiCanvas.width !== w || this.roiCanvas.height !== h) {
      this.roiCanvas = document.createElement('canvas');
      this.roiCanvas.width = w;
      this.roiCanvas.height = h;
      this.roiCtx = this.roiCanvas.getContext('2d');
    }
    return this.roiCtx;
  }

  private extractCheekRGB(
    videoElement: HTMLVideoElement,
    landmarks: Array<{ x: number; y: number; z: number }>
  ): { r: number; g: number; b: number } | null {
    const vw = videoElement.videoWidth;
    const vh = videoElement.videoHeight;
    if (vw === 0 || vh === 0) return null;

    const ctx = this.ensureMainCanvas(vw, vh);
    if (!ctx) return null;

    ctx.drawImage(videoElement, 0, 0);

    let rSum = 0, gSum = 0, bSum = 0, count = 0;

    for (const cheekIndices of [LEFT_CHEEK, RIGHT_CHEEK]) {
      const points = cheekIndices.map(idx => ({
        x: Math.floor(landmarks[idx].x * vw),
        y: Math.floor(landmarks[idx].y * vh),
      }));

      // Compute bounding box instead of scanning full frame
      let minX = vw, minY = vh, maxX = 0, maxY = 0;
      for (const p of points) {
        if (p.x < minX) minX = p.x;
        if (p.y < minY) minY = p.y;
        if (p.x > maxX) maxX = p.x;
        if (p.y > maxY) maxY = p.y;
      }
      minX = Math.max(0, minX);
      minY = Math.max(0, minY);
      maxX = Math.min(vw - 1, maxX);
      maxY = Math.min(vh - 1, maxY);

      const bboxW = maxX - minX + 1;
      const bboxH = maxY - minY + 1;
      if (bboxW <= 0 || bboxH <= 0) continue;

      const roiCtx = this.ensureROICanvas(bboxW, bboxH);
      if (!roiCtx) continue;

      // Draw cropped region only
      roiCtx.clearRect(0, 0, bboxW, bboxH);
      roiCtx.globalCompositeOperation = 'source-over';
      roiCtx.drawImage(this.mainCanvas!, minX, minY, bboxW, bboxH, 0, 0, bboxW, bboxH);

      // Apply polygon mask
      roiCtx.globalCompositeOperation = 'destination-in';
      roiCtx.beginPath();
      roiCtx.moveTo(points[0].x - minX, points[0].y - minY);
      for (let i = 1; i < points.length; i++) {
        roiCtx.lineTo(points[i].x - minX, points[i].y - minY);
      }
      roiCtx.closePath();
      roiCtx.fill();
      roiCtx.globalCompositeOperation = 'source-over';

      // Read bounding-box pixels only (not full-frame)
      const pixels = roiCtx.getImageData(0, 0, bboxW, bboxH).data;
      for (let i = 0; i < pixels.length; i += 4) {
        if (pixels[i + 3] > 0) {
          rSum += pixels[i];
          gSum += pixels[i + 1];
          bSum += pixels[i + 2];
          count++;
        }
      }
    }

    if (count === 0) return null;
    return { r: rSum / count, g: gSum / count, b: bSum / count };
  }
}
